import { useTheme } from '../../context/ThemeContext';
import { Sun, Moon } from 'lucide-react';
import { CONFIG } from '../../config';

/**
 * Navigation Bar Component
 * 
 * Top fixed navigation bar for desktop users.
 * Changes appearance on scroll (transparency/blur effect).
 * Contains links to sections and the dark mode toggle.
 */
const Navbar = ({ activeSection, setActiveSection, scrolled }) => {
    const { theme, toggleTheme } = useTheme();

    const navItems = [
        { id: 'home', label: 'Dashboard' },
        { id: 'experience', label: 'Experience' },
        { id: 'projects', label: 'Projects' },
        { id: 'services', label: 'Services' },
        { id: 'certifications', label: 'Certifications' },
        { id: 'about', label: 'Profile' },
    ];

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
            ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200 dark:border-white/5 shadow-lg'
            : 'bg-transparent'
            }`}>
            <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">

                {/* Brand Logo and Name */}
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <div className="absolute inset-0 bg-purple-500 blur-lg opacity-20 rounded-full"></div>
                        <img
                            src={CONFIG.personal.avatar}
                            alt="Profile"
                            className="relative w-10 h-10 rounded-full border border-gray-200 dark:border-white/10"
                        />
                    </div>
                    <span className="font-bold text-lg tracking-tight text-slate-900 dark:text-white hidden sm:block">
                        {CONFIG.personal.name}
                    </span>
                </div>

                {/* Desktop Navigation Links */}
                <div className="hidden md:flex items-center gap-1 bg-gray-100/50 dark:bg-white/5 p-1 rounded-full border border-gray-200 dark:border-white/5 backdrop-blur-sm">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeSection === item.id
                                ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/25'
                                : 'text-slate-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/5'
                                }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                {/* CTA Button & Theme Toggle */}
                <div className="hidden md:flex items-center gap-4">
                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-full text-slate-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                        aria-label="Toggle Theme"
                    >
                        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                    </button>

                    <a
                        href={`mailto:${CONFIG.personal.email}`}
                        className="px-6 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-full text-sm font-semibold hover:bg-slate-800 dark:hover:bg-gray-100 transition-colors"
                    >
                        Contact Me
                    </a>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
